import { SITE_URL, type FaqItem } from '@/lib/jsonld';

/**
 * Przewodniki — Polish explainer content for four high-AI-volume queries.
 *
 * Content lives here (not in src/i18n/locales/*.json) on purpose: the three
 * locale files are held at STRICT key parity by
 * src/test/diacritics-guard.test.ts, and four long-form articles would triple
 * that surface for zero lookup benefit. The guides are Polish-only — the
 * /en and /uk routes exist so navigation never 404s, and every one of them
 * canonicalises to the Polish URL (see ExplainerLayout).
 *
 * Factual discipline: every date below traces to an in-repo source, cited in
 * the `sources` field of each guide. Where the repo is silent on an exact
 * deadline the copy hedges ("zgodnie z aktualnym harmonogramem Ministerstwa
 * Finansów") rather than asserting a specific date it cannot support. Do NOT add
 * statute article numbers here unless you have verified them against
 * docs/compliance/.
 */

export interface GuideTable {
  /** Optional caption shown above the table (and as <caption> for AT). */
  caption?: string;
  headers: readonly string[];
  rows: readonly (readonly string[])[];
}

export interface GuideSubsection {
  heading: string;
  body: string[];
}

export interface GuideRelatedLink {
  href: string;
  label: string;
}

/** Funnel CTA — label must stay on the approved pack (`Załóż konto za darmo`). */
export interface GuideFunnelCta {
  label: string;
  priceLine?: string;
  microcopy?: string;
  heading?: string;
  body?: string;
  utmCampaign: string;
  utmContent: string;
}

export interface GuideSection {
  /** Stable anchor id — used for in-page nav and deep links from AI answers. */
  id: string;
  /** Question-led H2. AI engines match on the question, not on a noun phrase. */
  heading: string;
  /** Plain declarative paragraphs. Short sentences quote better. */
  body: string[];
  /** Optional scannable list rendered under the paragraphs. */
  bullets?: string[];
  /** Optional comparison / data table (rendered after body, before bullets). */
  table?: GuideTable;
  /** Optional H3 blocks for a section that compares several named models. */
  subsections?: GuideSubsection[];
}

export interface Guide {
  /** Path segment under /przewodniki/. */
  slug: string;
  /** <title> — includes the query terms a reader would type. */
  seoTitle: string;
  /** Meta description + og:description. */
  description: string;
  /** On-page H1. */
  h1: string;
  /** Short label used in the sibling-guide list, footer and breadcrumbs. Falls back to `h1`. */
  navLabel?: string;
  /**
   * The AI-quotable block: 2-3 plain sentences that answer the query outright,
   * placed above everything else. If an engine lifts one paragraph, this is it.
   */
  lead: string;
  /**
   * Optional product-limit / prepare-only line after the lead. Kept out of
   * `lead` so the AI-quotable block stays 2–4 sentences.
   */
  productLimit?: string;
  /** ISO date the guide was written. */
  datePublished: string;
  /** ISO date of the last factual review. */
  dateModified: string;
  sections: GuideSection[];
  faq: FaqItem[];
  /** One honest sentence about what Bookeper automates for this topic. */
  bookeper: string;
  /** Where the dates/claims in this guide were verified. Rendered on-page. */
  sources: string[];
  /** Optional longer disclaimer replacing the generic sources footnote. */
  disclaimer?: string;
  relatedLinks?: GuideRelatedLink[];
  earlyCta?: GuideFunnelCta;
  mentionCta?: GuideFunnelCta;
  footerCta?: GuideFunnelCta;
  stickyCta?: GuideFunnelCta;
}

const REVIEWED = '2026-08-05';

export const KPIR_ELEKTRONICZNA: Guide = {
  slug: 'kpir-elektroniczna-2026',
  seoTitle: 'KPiR elektroniczna 2026 — JPK_KPiR krok po kroku | Bookeper',
  description:
    'Od 2026 roku podatkowa księga przychodów i rozchodów musi być prowadzona elektronicznie i przesyłana jako JPK_KPiR. Kto podlega obowiązkowi, co trzeba przygotować i w jakich terminach.',
  h1: 'KPiR elektroniczna 2026 — kto musi, od kiedy i czym jest JPK_KPiR',
  navLabel: 'KPiR elektroniczna 2026',
  lead: 'Obowiązek prowadzenia podatkowej księgi przychodów i rozchodów (KPiR) wyłącznie przy użyciu programu komputerowego wchodzi etapami. Od roku podatkowego rozpoczynającego się po 31 grudnia 2025 obejmuje podatników, którzy co miesiąc składają JPK_V7M; pozostałych podatników PIT — od roku podatkowego rozpoczynającego się po 31 grudnia 2026. Od tego momentu papierowa księga i odręcznie uzupełniany zeszyt przestają spełniać wymóg ustawowy. Księgę przekazuje się urzędowi skarbowemu w strukturze JPK_PKPIR — potocznie nazywanej JPK_KPiR — po zakończeniu roku podatkowego, więc pierwsza wysyłka za 2026 rok następuje w 2027.',
  datePublished: '2026-08-05',
  dateModified: REVIEWED,
  sections: [
    {
      id: 'czym-jest-kpir',
      heading: 'Czym jest KPiR i kto ją prowadzi?',
      body: [
        'Podatkowa księga przychodów i rozchodów to uproszczona ewidencja księgowa. Zapisujesz w niej przychody ze sprzedaży oraz koszty uzyskania przychodu, a różnica między nimi jest podstawą do wyliczenia zaliczki na podatek dochodowy.',
        'KPiR prowadzą przede wszystkim jednoosobowe działalności gospodarcze (JDG) rozliczające się według skali podatkowej albo podatkiem liniowym, a także spółki cywilne, jawne i partnerskie osób fizycznych, o ile nie przekroczyły progu zobowiązującego do pełnych ksiąg rachunkowych.',
        'Przedsiębiorcy na ryczałcie od przychodów ewidencjonowanych nie prowadzą KPiR — prowadzą ewidencję przychodów, bo koszty nie wpływają u nich na wysokość podatku.',
      ],
    },
    {
      id: 'co-sie-zmienia',
      heading: 'Co dokładnie zmienia się od 2026 roku?',
      body: [
        'Zmieniają się dwie rzeczy naraz i łatwo je pomylić. Pierwsza: księga musi być prowadzona przy użyciu programu komputerowego — koniec z formą papierową. Druga: księgę trzeba przekazać urzędowi skarbowemu w ustandaryzowanej strukturze XML, a nie tylko trzymać ją u siebie na wypadek kontroli.',
        'Do tej pory KPiR w Excelu była w porządku, dopóki zawierała wymagane kolumny. Od 2026 roku sam arkusz to za mało, bo plik musi zostać wygenerowany w strukturze zgodnej ze schematem opublikowanym przez Ministerstwo Finansów i przejść jego walidację.',
        'Praktyczna konsekwencja jest taka, że dane muszą być kompletne przez cały rok, a nie porządkowane w marcu. Struktura wymaga między innymi identyfikacji kontrahenta i daty zdarzenia gospodarczego przy każdym zapisie — brakujących pól nie da się uzupełnić w ostatniej chwili z pamięci.',
      ],
    },
    {
      id: 'czym-jest-jpk-kpir',
      heading: 'Czym jest plik JPK_KPiR (JPK_PKPIR)?',
      body: [
        'JPK_PKPIR to Jednolity Plik Kontrolny zawierający zawartość Twojej księgi w formacie XML. Nie jest to deklaracja podatkowa i nie zastępuje zeznania rocznego — to zrzut ewidencji, który urząd może wczytać i przeanalizować maszynowo.',
        'Plik składa się z nagłówka (kto, za jaki okres, jaki urząd), sekcji z zapisami księgi odpowiadającymi kolumnom KPiR oraz sekcji podsumowań kontrolnych. Jeśli sumy kontrolne nie zgadzają się z zapisami, plik zostanie odrzucony na etapie walidacji.',
        'JPK_PKPIR to inna struktura niż JPK_V7, którą podatnicy VAT wysyłają co miesiąc. Obowiązek VAT-owski nie znika i nie jest przez nią zastępowany — to dwa niezależne raporty.',
      ],
      bullets: [
        'JPK_V7 — ewidencja VAT, wysyłana miesięcznie lub kwartalnie przez czynnych podatników VAT',
        'JPK_PKPIR — podatkowa księga przychodów i rozchodów, przekazywana po zakończeniu roku',
        'JPK_EWP — ewidencja przychodów dla ryczałtowców, odpowiednik JPK_PKPIR na ryczałcie',
        'JPK_KR — pełne księgi rachunkowe, dla podmiotów prowadzących księgi handlowe',
      ],
    },
    {
      id: 'kogo-dotyczy',
      heading: 'Kogo obowiązek obejmuje, a kogo nie?',
      body: [
        'Obowiązek elektronicznej księgi i wysyłki JPK dotyczy podatników prowadzących KPiR — czyli przede wszystkim JDG na skali i na podatku liniowym. Data wejścia zależy jednak od tego, czy składasz JPK_V7M: jeśli tak, obowiązek obejmuje Cię od roku podatkowego rozpoczynającego się po 31 grudnia 2025; jeśli nie jesteś czynnym podatnikiem VAT — dopiero od roku rozpoczynającego się po 31 grudnia 2026.',
        'Jeśli jesteś na ryczałcie, KPiR nie prowadzisz, ale nie oznacza to, że jesteś poza zakresem zmian — Twoim odpowiednikiem jest ewidencja przychodów przekazywana jako JPK_EWP, obejmowana analogiczną logiką elektronizacji.',
        'Osoby prowadzące pełne księgi rachunkowe raportują strukturę JPK_KR i mają własny, odrębny harmonogram. Konkretne terminy przekazania każdej z tych struktur warto sprawdzić na stronach Ministerstwa Finansów, bo były w ostatnich latach kilkukrotnie przesuwane.',
      ],
    },
    {
      id: 'jak-sie-przygotowac',
      heading: 'Jak przygotować się krok po kroku?',
      body: [
        'Przygotowanie sprowadza się do tego, żeby dane wychodzące z Twojej firmy były kompletne w momencie powstania, a nie rekonstruowane po roku.',
      ],
      bullets: [
        'Sprawdź, w czym dziś prowadzisz księgę i czy to narzędzie potrafi wygenerować plik XML w strukturze JPK_PKPIR.',
        'Uporządkuj dane kontrahentów — struktura wymaga identyfikacji strony transakcji, a „faktura od dostawcy z internetu” nie przejdzie walidacji.',
        'Ustal jeden sposób opisywania kosztów i trzymaj się go przez cały rok; niespójne kategorie to najczęstsza przyczyna rozjazdu między księgą a rzeczywistością.',
        'Zbieraj dokumenty na bieżąco, najlepiej cyfrowo. Wersja elektroniczna księgi zakłada, że dowód księgowy da się do zapisu podpiąć.',
        'Zrób próbne wygenerowanie pliku w trakcie roku, a nie po jego zakończeniu — walidator pokaże braki, gdy jeszcze można je uzupełnić.',
      ],
    },
    {
      id: 'najczestsze-bledy',
      heading: 'Jakie błędy pojawiają się najczęściej?',
      body: [
        'Najczęstszy błąd to traktowanie elektronizacji jako zmiany formatu pliku. To zmiana w tym, jak kompletne muszą być dane. Księga, która „się zgadza” na poziomie sum, potrafi nie przejść walidacji z powodu pustego pola przy jednym zapisie ze stycznia.',
        'Drugi błąd to mieszanie wydatków prywatnych i firmowych na jednym rachunku. Dopóki księga była Twoim dokumentem wewnętrznym, rozdzielało się je przy okazji. W strukturze przekazywanej urzędowi każdy zapis jest widoczny osobno.',
        'Trzeci błąd to odkładanie kwalifikacji kosztu — wpisanie wydatku „do wyjaśnienia” i pozostawienie go tak do końca roku. Koszt albo jest kosztem uzyskania przychodu, albo nim nie jest, i tę decyzję trzeba udokumentować.',
      ],
    },
  ],
  faq: [
    {
      q: 'Czy od 2026 roku mogę prowadzić KPiR w Excelu?',
      a: 'Sam arkusz kalkulacyjny przestaje wystarczać. Księga musi być prowadzona przy użyciu programu komputerowego i przekazana urzędowi w ustandaryzowanej strukturze XML (JPK_PKPIR), której zwykły plik Excela nie generuje. Excel może pozostać narzędziem roboczym, ale plik do urzędu musi powstać w programie potrafiącym wyprodukować i zwalidować strukturę JPK.',
    },
    {
      q: 'Czy KPiR papierową trzeba przepisać do systemu?',
      a: 'Księgi za lata wcześniejsze przechowuje się w formie, w jakiej były prowadzone — nie ma obowiązku wstecznej konwersji. Elektroniczna forma dotyczy księgi prowadzonej od momentu wejścia obowiązku w życie, czyli od roku podatkowego 2026.',
    },
    {
      q: 'Kiedy wysyła się JPK_KPiR do urzędu skarbowego?',
      a: 'Struktura JPK_PKPIR jest raportem rocznym, przekazywanym po zakończeniu roku podatkowego, a nie plikiem miesięcznym jak JPK_V7. Dokładny termin przekazania warto potwierdzić w aktualnym harmonogramie Ministerstwa Finansów, bo terminy raportowania JPK w podatkach dochodowych były w ostatnich latach przesuwane.',
    },
    {
      q: 'Czy ryczałtowiec też musi wysyłać JPK z księgi?',
      a: 'Ryczałtowiec nie prowadzi KPiR, więc nie wysyła JPK_PKPIR. Jego odpowiednikiem jest ewidencja przychodów przekazywana w strukturze JPK_EWP. Zakres danych jest węższy, bo na ryczałcie koszty nie wpływają na wysokość podatku.',
    },
    {
      q: 'Czy obowiązek JPK_KPiR zastępuje JPK_V7?',
      a: 'Nie. To dwie niezależne struktury. JPK_V7 dotyczy ewidencji VAT i jest składany miesięcznie lub kwartalnie przez czynnych podatników VAT. JPK_PKPIR dotyczy podatku dochodowego i księgi przychodów i rozchodów. Podatnik VAT prowadzący KPiR składa oba.',
    },
    {
      q: 'Co się stanie, jeśli plik nie przejdzie walidacji?',
      a: 'Plik odrzucony przez walidator jest traktowany jak niezłożony, więc trzeba poprawić dane i wysłać go ponownie. Dlatego warto wygenerować plik testowo w trakcie roku — walidator pokaże brakujące pola, kiedy jeszcze da się je uzupełnić u źródła.',
    },
  ],
  bookeper:
    'Bookeper prowadzi KPiR w formie elektronicznej od pierwszego zapisu, kwalifikuje każdy koszt jako KUP albo NKUP wraz z uzasadnieniem i generuje plik JPK_PKPIR walidowany schematem Ministerstwa Finansów. Plik przygotowujemy do wysyłki — decyzję o jego złożeniu zawsze zatwierdzasz Ty.',
  sources: [
    'Ministerstwo Finansów / KAS — Struktury JPK w podatkach dochodowych (JPK_PKPIR; obowiązek od roku podatkowego rozpoczynającego się po 31.12.2025 dla składających JPK_V7M, po 31.12.2026 dla pozostałych podatników PIT, pierwsza wysyłka w 2027)',
    'Portal Podatkowy — pliki do pobrania JPK_PD',
  ],
};

export const KOD_PKD: Guide = {
  slug: 'kod-pkd-jak-wybrac',
  seoTitle: 'Kod PKD — jak wybrać właściwy przy rejestracji firmy | Bookeper',
  description:
    'Jak wybrać kod PKD przy zakładaniu działalności: kod przeważający i dodatkowe, przykłady dla popularnych zawodów, zmiana kodu w CEIDG i przejście na PKD 2025.',
  h1: 'Kod PKD — jak wybrać właściwy dla swojej działalności',
  navLabel: 'Kod PKD — jak wybrać',
  lead: 'Kod PKD opisuje, czym faktycznie zajmuje się Twoja firma. Przy rejestracji w CEIDG wskazujesz jeden kod przeważający — ten, z którego spodziewasz się największych przychodów — oraz dowolną liczbę kodów dodatkowych. Od 1 stycznia 2025 roku nowe wpisy stosują klasyfikację PKD 2025; wpisy założone wcześniej można zaktualizować samodzielnie do końca 2026 roku, a od 1 stycznia 2027 pozostałe kody zostaną przeklasyfikowane automatycznie.',
  datePublished: '2026-08-05',
  dateModified: REVIEWED,
  sections: [
    {
      id: 'czym-jest-pkd',
      heading: 'Czym jest kod PKD i do czego służy?',
      body: [
        'PKD to Polska Klasyfikacja Działalności — słownik, w którym każdy rodzaj działalności gospodarczej ma przypisany symbol, na przykład 62.01.Z. Kod trafia do wpisu w CEIDG i jest widoczny publicznie.',
        'PKD pełni przede wszystkim funkcję statystyczną: państwo chce wiedzieć, ilu jest w kraju programistów, a ilu fryzjerów. Ale kod bywa też czytany przez banki przy wniosku o kredyt, przez ubezpieczycieli, przez zamawiających w przetargach i przez instytucje przyznające dotacje.',
        'Kod nie ogranicza tego, co wolno Ci robić w sensie cywilnoprawnym, ale wpis powinien odpowiadać rzeczywistości. Wystawianie faktur za usługi, których nie ma nigdzie w Twoim wpisie, jest sygnałem rozjazdu między dokumentami a stanem faktycznym.',
      ],
    },
    {
      id: 'przewazajacy-i-dodatkowe',
      heading: 'Czym różni się kod przeważający od dodatkowych?',
      body: [
        'Kod przeważający to jeden kod, z którego spodziewasz się największej części przychodów. W CEIDG może być tylko jeden i to on trafia do statystyk jako Twoja główna działalność.',
        'Kody dodatkowe opisują wszystko pozostałe, czym się zajmujesz lub planujesz zajmować. Nie ma opłaty za każdy kolejny, ale nie warto wpisywać dziesiątek kodów „na zapas” — wpis, który obejmuje jednocześnie doradztwo IT, transport drogowy i handel detaliczny, wygląda niewiarygodnie dla każdego, kto go czyta.',
        'Kod przeważający można zmienić, kiedy zmieni się profil firmy. Jeśli zaczynałeś od szkoleń, a dziś większość przychodu pochodzi z oprogramowania, wpis powinien to odzwierciedlać.',
      ],
    },
    {
      id: 'jak-wybrac',
      heading: 'Jak wybrać kod PKD krok po kroku?',
      body: [
        'Zacznij od zdania opisującego, za co klient Ci płaci — nie od nazwy zawodu. „Piszę backend dla aplikacji webowych” prowadzi do innego kodu niż „szkolę zespoły z programowania”, choć obie rzeczy robi programista.',
      ],
      bullets: [
        'Napisz jednym zdaniem, co dokładnie sprzedajesz i komu.',
        'Wyszukaj to zdanie w wyszukiwarce klasyfikacji PKD — szukaj po opisie czynności, nie po nazwie branży.',
        'Sprawdź opis wyjaśniający wybranego kodu: klasyfikacja przy większości pozycji wskazuje, co do niej należy i co jest z niej wyłączone.',
        'Wybierz jako przeważający ten kod, który odpowiada największej części spodziewanych przychodów.',
        'Dodaj kody dodatkowe dla realnych, a nie hipotetycznych, kierunków działalności.',
        'Przed złożeniem wniosku potwierdź aktualne brzmienie symbolu — klasyfikacja zmieniła się wraz z wejściem PKD 2025.',
      ],
    },
    {
      id: 'przyklady',
      heading: 'Jakie kody wybierają popularne działalności?',
      body: [
        'Poniższe przykłady są poglądowe i mają pokazać sposób myślenia, a nie zastąpić sprawdzenia w wyszukiwarce PKD. Przy każdej działalności decyduje faktycznie wykonywana czynność.',
      ],
      bullets: [
        'Programista, wytwarzanie oprogramowania na zlecenie — grupa 62.01 (działalność związana z oprogramowaniem).',
        'Fryzjerstwo i pozostałe zabiegi kosmetyczne — 96.02.Z.',
        'Usługi krawieckie rozumiane jako naprawa i przeróbki odzieży — 95.29.Z (naprawa pozostałych artykułów użytku osobistego i domowego); szycie odzieży od podstaw to natomiast produkcja, a nie naprawa, i ma własny kod.',
        'Projektowanie graficzne i wzornictwo — 74.10.Z (działalność w zakresie specjalistycznego projektowania).',
        'Wykonywanie instalacji elektrycznych — 43.21.Z.',
        'Prowadzenie szkoleń i kursów poza systemem szkolnym — grupa 85.59 (pozostałe pozaszkolne formy edukacji).',
      ],
    },
    {
      id: 'zmiana-w-ceidg',
      heading: 'Jak zmienić kod PKD w CEIDG?',
      body: [
        'Zmiana kodu to wniosek o zmianę wpisu w CEIDG. Składasz go elektronicznie przez profil zaufany albo podpis kwalifikowany, ewentualnie osobiście w urzędzie gminy. Wniosek jest bezpłatny.',
        'We wniosku wskazujesz kody do dodania i do usunięcia oraz — jeśli się zmienia — nowy kod przeważający. Zmiana powinna zostać zgłoszona w terminie liczonym od dnia faktycznej zmiany, więc nie warto z nią czekać do końca roku.',
        'CEIDG przekazuje zmianę dalej do urzędu skarbowego, ZUS i GUS, więc nie trzeba zgłaszać jej osobno w każdej z tych instytucji.',
      ],
    },
    {
      id: 'pkd-2025',
      heading: 'Co zmieniło PKD 2025?',
      body: [
        'PKD 2025 zastąpiło klasyfikację PKD 2007. Nowe wpisy zakładane od 1 stycznia 2025 roku posługują się już nową klasyfikacją.',
        'Firmy zarejestrowane wcześniej mają czas na samodzielną aktualizację kodów do końca 2026 roku. Od 1 stycznia 2027 roku kody, które nie zostały zaktualizowane, mają zostać przeklasyfikowane automatycznie na podstawie tablic przejścia.',
        'Automatyczne przeklasyfikowanie nie zawsze trafia idealnie, bo część starych kodów rozpada się na kilka nowych. Jeśli Twój kod ma taki rozpad, lepiej wybrać nowy symbol samodzielnie, niż zdać się na tablicę.',
      ],
    },
    {
      id: 'pkd-a-podatki',
      heading: 'Czy kod PKD decyduje o stawce ryczałtu?',
      body: [
        'Nie, i to jest najczęstsze nieporozumienie wokół PKD. Stawki ryczałtu od przychodów ewidencjonowanych są przypisane do symboli PKWiU — Polskiej Klasyfikacji Wyrobów i Usług — a nie do kodów PKD.',
        'PKD i PKWiU to dwie różne klasyfikacje o różnej strukturze i między PKD 2025 a PKWiU 2015 nie ma bezpośredniego, jednoznacznego przełożenia. Dobranie stawki „z kodu PKD” prowadzi więc do błędów, zwłaszcza tam, gdzie jeden kod PKD obejmuje usługi opodatkowane różnymi stawkami.',
        'Praktyczny wniosek: kod PKD ustalasz dla wpisu w CEIDG, a stawkę ryczałtu ustalasz osobno, od strony PKWiU i faktycznego charakteru świadczonej usługi.',
      ],
    },
  ],
  faq: [
    {
      q: 'Ile kodów PKD można mieć w jednym wpisie?',
      a: 'Kod przeważający jest zawsze jeden. Kodów dodatkowych CEIDG nie limituje w praktyczny sposób i nie ma za nie opłaty. Warto jednak wpisywać tylko te, które odpowiadają realnie prowadzonej albo realnie planowanej działalności.',
    },
    {
      q: 'Czy trzeba mieć kod PKD, żeby wystawić fakturę za daną usługę?',
      a: 'Faktura wystawiona za usługę spoza wpisu nie jest z automatu nieważna, ale rozjazd między wpisem a rzeczywistą działalnością jest sygnałem ostrzegawczym przy kontroli i przy weryfikacji przez banki czy zamawiających. Jeśli usługa staje się powtarzalna, dopisz odpowiedni kod.',
    },
    {
      q: 'Czy zmiana kodu PKD kosztuje?',
      a: 'Nie. Wniosek o zmianę wpisu w CEIDG jest bezpłatny, niezależnie od tego, czy dodajesz kody, usuwasz je, czy zmieniasz kod przeważający.',
    },
    {
      q: 'Czy kod PKD wpływa na wysokość składek ZUS?',
      a: 'Kod PKD sam w sobie nie ustala wysokości składek. Wysokość składek zależy od wybranej podstawy i od tego, czy korzystasz z ulg na starcie działalności. Kod może natomiast mieć znaczenie dla stawki ubezpieczenia wypadkowego przy zatrudnianiu pracowników.',
    },
    {
      q: 'Mam wpis z 2019 roku — czy muszę już teraz zmienić kody na PKD 2025?',
      a: 'Samodzielna aktualizacja jest możliwa do końca 2026 roku. Od 1 stycznia 2027 roku niezaktualizowane kody mają zostać przeklasyfikowane automatycznie. Jeśli Twój dotychczasowy kod odpowiada w nowej klasyfikacji kilku różnym symbolom, lepiej wybrać nowy kod samodzielnie.',
    },
    {
      q: 'Skąd wziąć aktualną listę kodów PKD?',
      a: 'Z wyszukiwarki klasyfikacji udostępnianej przy wniosku CEIDG oraz z tekstu klasyfikacji PKD 2025. Listy kopiowane z blogów bywają przestarzałe — po wejściu PKD 2025 część symboli spotykanych w starszych poradnikach już nie istnieje.',
    },
  ],
  bookeper:
    'Bookeper normalizuje kod PKD z Twojego wpisu i sprawdza, czy usługa, którą fakturujesz, mieści się w profilu działalności, a stawkę ryczałtu ustala od strony PKWiU — nie zgaduje jej z kodu PKD. Każde ustalenie pokazujemy razem ze ścieżką, którą do niego doszliśmy.',
  sources: [
    'Klasyfikacja PKD 2025 — w mocy od 1 stycznia 2025, aktualizacja wpisów do 31 grudnia 2026, automatyczne przeklasyfikowanie od 1 stycznia 2027',
    'Ustawa o zryczałtowanym podatku dochodowym (uZPD) — stawki ryczałtu przypisane do symboli PKWiU, nie do kodów PKD',
    'GUS — tablice przejścia PKD 2025, publikowane wraz z klasyfikacją; brak bezpośredniego klucza PKD 2025 ↔ PKWiU 2015',
  ],
};

export const KSEF_DLA_JDG: Guide = {
  slug: 'ksef-dla-jdg',
  seoTitle: 'KSeF dla JDG — od kiedy obowiązek i co trzeba zrobić | Bookeper',
  description:
    'KSeF dla jednoosobowej działalności: harmonogram wejścia obowiązku, uwierzytelnianie i token, jak zmienia się wystawianie faktur i co zostaje poza systemem.',
  h1: 'KSeF dla JDG — od kiedy obowiązek i co trzeba zrobić',
  navLabel: 'KSeF dla JDG',
  lead: 'KSeF, czyli Krajowy System e-Faktur, to rządowa platforma, przez którą przechodzą faktury ustrukturyzowane w formacie XML. Zgodnie z harmonogramem Ministerstwa Finansów obowiązek wystawiania faktur w KSeF wchodzi etapami: od 1 lutego 2026 roku obejmuje podatników o największej sprzedaży, a od 1 kwietnia 2026 roku pozostałych przedsiębiorców — również tych zwolnionych z VAT. Do końca 2026 roku obowiązuje jeszcze okres przejściowy dla sprzedaży udokumentowanej fakturami objętymi KSeF do 10 000 zł brutto miesięcznie. Dla JDG oznacza to, że fakturę objętą obowiązkiem KSeF wystawiasz w swoim programie i wysyłasz do systemu — w trybie online datą wystawienia jest dzień przesłania, a nie moment nadania numeru KSeF.',
  datePublished: '2026-08-05',
  dateModified: REVIEWED,
  sections: [
    {
      id: 'czym-jest-ksef',
      heading: 'Czym jest KSeF?',
      body: [
        'KSeF to centralna baza faktur prowadzona przez Ministerstwo Finansów. Faktura nie jest już plikiem PDF, który wysyłasz mailem — jest dokumentem XML o ustalonej strukturze, przyjętym przez system i opatrzonym unikalnym numerem KSeF.',
        'System działa dwustronnie. Wysyłasz przez niego faktury sprzedażowe i przez niego odbierasz faktury od dostawców. Faktura zakupowa nie przychodzi już mailem — czeka na Ciebie w systemie.',
        'Dla organów podatkowych KSeF oznacza wgląd w obrót w czasie zbliżonym do rzeczywistego. Dla przedsiębiorcy oznacza, że moment „wystawienia” faktury przestaje być czynnością wykonywaną w edytorze, a staje się zdarzeniem potwierdzonym przez system.',
      ],
    },
    {
      id: 'harmonogram',
      heading: 'Od kiedy KSeF jest obowiązkowy dla JDG?',
      body: [
        'Harmonogram jest etapowy i zależy od wielkości sprzedaży, a nie od formy prawnej ani od statusu VAT. Jednoosobowa działalność nie ma z tego tytułu osobnej ścieżki, a zwolnienie z VAT — podmiotowe czy przedmiotowe — nie zwalnia z KSeF.',
        'Zgodnie z aktualnym harmonogramem Ministerstwa Finansów kolejność jest następująca:',
      ],
      bullets: [
        '1 lutego 2026 — obowiązek dla podatników o sprzedaży za 2024 rok powyżej 200 mln zł brutto.',
        '1 kwietnia 2026 — obowiązek dla pozostałych przedsiębiorców, w tym podatników zwolnionych z VAT (podmiotowo i przedmiotowo); to etap, który obejmuje zdecydowaną większość JDG.',
        'Do 31 grudnia 2026 — okres przejściowy: jeżeli łączna wartość sprzedaży udokumentowanej fakturami objętymi obowiązkiem KSeF nie przekracza w danym miesiącu 10 000 zł brutto, można jeszcze wystawiać faktury elektroniczne lub papierowe. Do limitu nie wliczają się m.in. faktury konsumenckie, sprzedaż z kasy fiskalnej i faktury uproszczone, a obowiązek zaczyna się od faktury, którą limit zostaje przekroczony. Od 1 stycznia 2027 ta możliwość się kończy.',
      ],
    },
    {
      id: 'co-musisz-zrobic',
      heading: 'Co musi zrobić JDG przed wejściem obowiązku?',
      body: [
        'Lista jest krótsza, niż się wydaje, ale każdy punkt trzeba faktycznie odhaczyć — nie da się ich zrobić w dniu wejścia obowiązku.',
      ],
      bullets: [
        'Ustal, w którym etapie harmonogramu się znajdujesz — to zależy od statusu VAT i wielkości sprzedaży.',
        'Sprawdź, czy program, w którym dziś wystawiasz faktury, umie wysyłać je do KSeF i odbierać z niego faktury zakupowe.',
        'Zadbaj o sposób uwierzytelnienia: profil zaufany, podpis kwalifikowany, pieczęć kwalifikowana albo token KSeF wygenerowany w systemie.',
        'Uzupełnij dane kontrahentów o numery NIP — faktura ustrukturyzowana identyfikuje strony po numerze, nie po nazwie firmy.',
        'Przećwicz wystawienie i odbiór faktury w środowisku testowym, zanim zrobisz to na dokumencie, który ma realne skutki podatkowe.',
        'Ustal, co robisz w razie awarii systemu — przepisy przewidują tryb offline, ale trzeba wiedzieć, jak z niego skorzystać.',
      ],
    },
    {
      id: 'uwierzytelnianie',
      heading: 'Jak uzyskać dostęp i czym jest token KSeF?',
      body: [
        'Do KSeF logujesz się jako podatnik albo jako osoba czy program upoważniony przez podatnika. Osoba fizyczna prowadząca JDG ma w tym najprostszą sytuację — może uwierzytelnić się profilem zaufanym lub podpisem kwalifikowanym i od razu ma pełne uprawnienia właścicielskie.',
        'Token KSeF to długi ciąg znaków wygenerowany w systemie, który pozwala programowi księgowemu łączyć się z KSeF bez każdorazowego logowania człowieka. Token jest poświadczeniem dostępu do Twoich faktur, więc trzeba go traktować jak hasło: nie wysyłać mailem, nie trzymać w pliku tekstowym i unieważnić, gdy zmieniasz dostawcę oprogramowania.',
        'Uprawnienia można nadawać zakresowo — osobno do wystawiania, osobno do odczytu. Jeśli współpracujesz z biurem rachunkowym, nadajesz mu uprawnienie w systemie, zamiast przekazywać własne dane logowania.',
      ],
    },
    {
      id: 'jak-zmienia-sie-fakturowanie',
      heading: 'Jak zmienia się wystawianie faktur na co dzień?',
      body: [
        'Zmienia się moment wystawienia. Do tej pory fakturą było to, co zapisałeś i wysłałeś klientowi. W trybie online datą wystawienia jest dzień przesłania faktury do KSeF; w trybie offline liczy się data wskazana w polu P_1, a fakturę dosyłasz do systemu w wymaganym terminie. Numer KSeF nadawany po przyjęciu dokumentu nie przesuwa daty wystawienia — wyznacza moment otrzymania faktury przez nabywcę (poza przypadkami udostępnienia faktury poza KSeF) i to on, a nie Twoja własna numeracja, identyfikuje dokument w obrocie.',
        'Zmienia się też korygowanie. Korekta również przechodzi przez system i wiąże się z konkretnym numerem KSeF faktury pierwotnej, więc „poprawię i wyślę jeszcze raz” przestaje być opcją.',
        'Twoja wewnętrzna numeracja faktur nie znika i nadal ma znaczenie porządkowe. Klientowi możesz nadal wysyłać wizualizację faktury w PDF — ale dokumentem w rozumieniu przepisów jest ta w KSeF.',
      ],
    },
    {
      id: 'co-zostaje-poza',
      heading: 'Co zostaje poza KSeF?',
      body: [
        'KSeF obejmuje faktury w obrocie między firmami. Sprzedaż na rzecz konsumentów oraz paragony z kasy fiskalnej rządzą się osobnymi zasadami i nie wchodzą do systemu na tych samych warunkach.',
        'Poza systemem pozostają też dokumenty, które fakturami nie są: noty księgowe, umowy, potwierdzenia przelewów. To istotne, bo KSeF bywa mylnie traktowany jak archiwum całej dokumentacji firmy.',
        'Zakres wyłączeń jest jednym z elementów, które w toku wdrożenia były doprecyzowywane, więc szczegółowe przypadki brzegowe warto potwierdzać na stronach Ministerstwa Finansów, a nie w starszych poradnikach.',
      ],
    },
  ],
  faq: [
    {
      q: 'Czy JDG bez VAT też musi korzystać z KSeF?',
      a: 'Tak. Zwolnienie z VAT — podmiotowe (limit 200 000 zł) czy przedmiotowe — nie zwalnia z KSeF. Od 1 kwietnia 2026 roku obowiązek obejmuje pozostałych przedsiębiorców, w tym podatników zwolnionych z VAT. Jedyne odroczenie to okres przejściowy do 31 grudnia 2026 roku dla sprzedaży udokumentowanej fakturami objętymi obowiązkiem KSeF do 10 000 zł brutto miesięcznie (nie licząc m.in. faktur konsumenckich i sprzedaży z kasy fiskalnej). Niezależnie od własnej daty granicznej, faktury zakupowe od kontrahentów już objętych obowiązkiem KSeF trafią tam wcześniej — dostęp do systemu przydaje się przed nią.',
    },
    {
      q: 'Czy po wejściu KSeF mogę dalej wysyłać klientowi PDF?',
      a: 'Możesz wysłać klientowi wizualizację faktury, na przykład w PDF, dla wygody. Dokumentem w rozumieniu przepisów jest jednak faktura ustrukturyzowana w KSeF, opatrzona numerem KSeF, a nie plik przesłany mailem.',
    },
    {
      q: 'Co się dzieje, gdy KSeF jest niedostępny?',
      a: 'Przepisy przewidują tryb pracy offline i awaryjny, w którym fakturę wystawia się poza systemem i przesyła do KSeF po przywróceniu dostępności, w wyznaczonym terminie. Warto sprawdzić, czy Twój program obsługuje ten tryb automatycznie, zanim awaria faktycznie nastąpi.',
    },
    {
      q: 'Czym jest numer KSeF i czy zastępuje moją numerację faktur?',
      a: 'Numer KSeF to identyfikator nadawany przez system w momencie przyjęcia faktury. Nie zastępuje Twojej wewnętrznej numeracji, która nadal porządkuje dokumenty, ale to on identyfikuje fakturę w obrocie i to do niego odwołują się korekty.',
    },
    {
      q: 'Czy mogę dać biuru rachunkowemu dostęp do moich faktur w KSeF?',
      a: 'Tak, i jest to właściwa droga. W systemie nadaje się uprawnienia konkretnemu podmiotowi lub osobie, w wybranym zakresie. Przekazywanie własnego tokenu albo danych logowania nie jest do tego potrzebne i niepotrzebnie zwiększa ryzyko.',
    },
    {
      q: 'Jak przygotować dane kontrahentów przed startem?',
      a: 'Najważniejsze są poprawne numery NIP, bo faktura ustrukturyzowana identyfikuje strony po numerze. Braki i literówki w NIP, które przy fakturze PDF przechodziły niezauważone, w KSeF zatrzymują dokument.',
    },
  ],
  bookeper:
    'Bookeper przygotowuje faktury w strukturze wymaganej przez KSeF, obsługuje uwierzytelnianie tokenem i śledzi status każdego dokumentu aż do UPO. Integrację rozwijamy i testujemy w środowisku KSeF — wysyłkę dokumentu zawsze zatwierdzasz Ty.',
  sources: [
    'Ministerstwo Finansów — portal KSeF, harmonogram obowiązku: 1 lutego 2026 (sprzedaż za 2024 powyżej 200 mln zł brutto), 1 kwietnia 2026 (pozostali przedsiębiorcy, w tym zwolnieni z VAT), okres przejściowy do 31 grudnia 2026 dla sprzedaży udokumentowanej fakturami objętymi KSeF do 10 000 zł brutto miesięcznie',
  ],
};

const FUNNEL_CTA_LABEL = 'Załóż konto za darmo';
const BOOKEPER_PRICE_LINE = '0 zł w otwartej becie';

export const KSIEGOWOSC_ONLINE: Guide = {
  slug: 'ksiegowosc-online',
  seoTitle: 'Księgowość online JDG 2026 – ile kosztuje vs biuro',
  description:
    'Ile kosztuje księgowość online dla JDG? Porównanie: biuro rachunkowe, program do samodzielnej księgowości i Bookeper — 0 zł w otwartej becie.',
  h1: 'Księgowość online dla JDG — co obejmuje i ile realnie kosztuje',
  navLabel: 'Księgowość online',
  lead: 'Księgowość online to prowadzenie ewidencji i rozliczeń JDG w aplikacji zamiast w segregatorze. W praktyce obejmuje trzy rzeczy: zapis zdarzeń (ewidencja przychodów albo KPiR), wyliczenie podatku / VAT / ZUS oraz przygotowanie deklaracji do Twojego sprawdzenia. Pytanie „ile kosztuje księgowa?” sprowadza się do wyboru modelu: biuro, program DIY albo Bookeper — i do tego, ile kroków system robi za Ciebie.',
  productLimit:
    'Bookeper jest dla prostej JDG (ryczałt, liniowy, skala). Przygotowujemy deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem; obsługa kolejnych formularzy jest rozwijana. Bookeper nie zastępuje doradcy podatkowego.',
  datePublished: '2026-08-05',
  dateModified: '2026-10-07',
  earlyCta: {
    label: FUNNEL_CTA_LABEL,
    priceLine: BOOKEPER_PRICE_LINE,
    microcopy:
      'Przygotowujemy deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem.',
    utmCampaign: 'ksiegowosc_online',
    utmContent: 'lead_inline',
  },
  sections: [
    {
      id: 'ile-kosztuje',
      heading: 'Ile kosztuje księgowa / księgowość JDG w 2026',
      body: [
        'Ceny biur i programów zależą od zakresu usług (VAT, liczba dokumentów, pracownicy), dlatego nie podajemy uśrednionych widełek. Poproś o wycenę konkretnego pakietu i porównaj, co wchodzi w cenę. Bookeper: 0 zł w otwartej becie.',
        'Różnicę policz na cenniku swojego biura i programów, które rozważasz. Jeśli potrzebujesz parasola OC albo masz skomplikowaną sprawę, biuro może być lepszym wyborem, nawet gdy kosztuje więcej.',
      ],
      table: {
        caption: 'Porównanie modeli: biuro, program do samodzielnej księgowości, Bookeper',
        headers: ['Model', 'Koszt', 'Co zwykle dostajesz', 'Twój czas'],
        rows: [
          [
            'Biuro rachunkowe',
            'zależy od pakietu i zakresu (VAT, liczba dokumentów) — poproś o wycenę',
            'Ewidencja, deklaracje, kontakt z US/ZUS; odpowiedzialność kontraktowa (OC) po stronie biura',
            'Niski na operacjach',
          ],
          [
            'Program DIY (samodzielnie)',
            'zależy od programu i pakietu — sprawdź cennik dostawcy',
            'Ewidencja, terminy, deklaracje i JPK generowane z danych — dokumenty wprowadzasz Ty',
            'Zależny od liczby dokumentów',
          ],
          [
            'Bookeper',
            '0 zł w otwartej becie',
            'Ewidencja, wyliczenia, przygotowanie deklaracji do zatwierdzenia',
            'Niski–średni (przegląd + zatwierdzenie)',
          ],
        ],
      },
    },
    {
      id: 'trzy-modele',
      heading: 'Trzy modele — co wybierasz',
      body: [
        'Te same zadania — ewidencja, należny podatek albo ryczałt, ZUS, przygotowanie deklaracji — inny udział Twojego czasu i odpowiedzialności.',
      ],
      subsections: [
        {
          heading: '1. Biuro rachunkowe',
          body: [
            'Oddajesz rozliczenia człowiekowi. Biuro prowadzi ewidencję, składa deklaracje, kontaktuje się z urzędem. Bierze odpowiedzialność kontraktową za swoją pracę (OC zawodowe). Pasuje, gdy chcesz maksymalnie odciążyć głowę albo masz sytuacje nietypowe.',
          ],
        },
        {
          heading: '2. Program DIY',
          body: [
            'Sam wprowadzasz dokumenty i pilnujesz terminów. Wiele programów generuje deklaracje z ewidencji i wysyła je do urzędu (z UPO) po Twoim kliknięciu. Zwykle taniej niż biuro, ale kosztuje Twój czas — ile dokładnie, zależy od liczby dokumentów.',
          ],
        },
        {
          heading: '3. Bookeper',
          body: [
            'Wrzucasz faktury — system liczy podatki i składki, prowadzi ewidencję i przygotowuje deklaracje. Ty sprawdzasz i zatwierdzasz przed złożeniem. Obsługujemy ryczałt, liniowy i skalę. Obsługa kolejnych formularzy jest rozwijana.',
            `Cena: ${BOOKEPER_PRICE_LINE}. Na dziś przygotowujemy deklaracje do Twojego zatwierdzenia — nie składamy ich za Ciebie.`,
          ],
        },
      ],
    },
    {
      id: 'porownanie',
      heading: 'Porównanie punkt po punkcie',
      body: ['Te same cztery osie: koszt, czas, odpowiedzialność, deklaracje.'],
      subsections: [
        {
          heading: 'Koszt',
          body: [
            'Cena Bookeper to 0 zł w otwartej becie. Żeby porównać koszt, zestaw tę kwotę z wyceną swojego biura albo cennikiem programu i sprawdź, co wchodzi w cenę.',
          ],
        },
        {
          heading: 'Czas',
          body: [
            'Biuro: najmniej Twojego czasu na wprowadzaniu dokumentów. DIY: zależnie od liczby dokumentów — bez stałej obietnicy „X minut miesięcznie”. Bookeper: przegląd i zatwierdzenie; bez przepisywania PDF-ów do Excela.',
          ],
        },
        {
          heading: 'Odpowiedzialność',
          body: [
            'Wobec US/ZUS odpowiadasz Ty jako podatnik — niezależnie od narzędzia. Biuro: dodatkowo kontrakt + OC zawodowe za błędy biura. Program / Bookeper: narzędzie; Ty zatwierdzasz. Bookeper pokazuje wyliczenia, żebyś mógł sprawdzić przed złożeniem. Bookeper nie zastępuje doradcy podatkowego.',
          ],
        },
        {
          heading: 'Deklaracje',
          body: [
            'Biuro: zwykle pełen zakres deklaracji w ramach umowy. Programy DIY: wiele z nich generuje i wysyła deklaracje (z UPO) po Twoim kliknięciu. Bookeper: przygotowujemy deklaracje na podstawie Twoich danych; każdą sprawdzasz i zatwierdzasz przed złożeniem; obsługa kolejnych formularzy jest rozwijana. Nie obiecujemy, że składamy albo wysyłamy wszystko do urzędu z UPO.',
          ],
        },
      ],
    },
    {
      id: 'kiedy-bookeper',
      heading: 'Kiedy Bookeper wystarczy',
      body: ['Bookeper ma sens, gdy:'],
      bullets: [
        'prowadzisz prostą JDG (bez pracowników),',
        'rozliczasz się na ryczałcie, liniowym albo skali,',
        'chcesz prowadzić ewidencję i rozliczenia bez biura,',
        'akceptujesz, że deklaracje przygotowujemy, a Ty je sprawdzasz i zatwierdzasz przed złożeniem; kolejne formularze są rozwijane (to nie „pełna księgowa AI od dnia 1”).',
      ],
    },
    {
      id: 'kiedy-biuro',
      heading: 'Kiedy lepiej zostać przy biurze',
      body: ['Rozważ biuro, gdy:'],
      bullets: [
        'masz spór z US/ZUS albo nietypową historię podatkową,',
        'potrzebujesz odpowiedzialności kontraktowej / OC po stronie księgowego,',
        'zatrudniasz ludzi, prowadzisz złożony VAT / handel / struktury, których Bookeper jeszcze nie obsługuje,',
        'wolisz oddać temat w 100% i nie zaglądać do aplikacji.',
      ],
    },
    {
      id: 'jak-wybrac',
      heading: 'Jak wybrać księgowość online (checklista)',
      body: ['Zanim klikniesz „załóż konto” u kogokolwiek:'],
      bullets: [
        'Jaka jest cena po starcie — nie tylko „za darmo na start”?',
        'Czy złożenie deklaracji wymaga Twojego zatwierdzenia?',
        'Czy obiecują pełne UPO / „składamy wszystko”, choć produkt tego nie ma?',
        'Czy jest sztuczna presja (np. licznik „zostały 3 miejsca”) albo zawstydzający komunikat przy rezygnacji?',
        'Czy rejestracja wymaga NIP, zanim zobaczysz produkt? (warto zapytać dostawcę — nie zakładaj, że wszędzie wygląda to tak samo)',
      ],
    },
  ],
  faq: [
    {
      q: 'Ile kosztuje księgowa dla JDG miesięcznie?',
      a: 'To zależy od zakresu: liczby dokumentów, VAT-u i tego, czy biuro bierze odpowiedzialność kontraktową. Poproś biuro o wycenę konkretnego pakietu i porównuj to, co wchodzi w cenę, a nie samą kwotę z reklamy.',
    },
    {
      q: 'Ile kosztuje księgowość online w Bookeper?',
      a: '0 zł w otwartej becie. Każdą deklarację sprawdzasz i zatwierdzasz przed złożeniem.',
    },
    {
      q: 'Czy Bookeper to pełna księgowość jak w biurze?',
      a: 'Nie w sensie OC zawodowego i pełnego zakresu każdej sprawy. To automatyczna ewidencja i przygotowanie deklaracji dla prostej JDG z Twoim zatwierdzeniem. Biuro zostaje lepsze przy złożoności i parasolu odpowiedzialności.',
    },
    {
      q: 'Czy Bookeper składa deklaracje za mnie z UPO?',
      a: 'Przygotowujemy deklaracje na podstawie Twoich danych. Każdą sprawdzasz i zatwierdzasz przed złożeniem; obsługa kolejnych formularzy jest rozwijana. Nie obiecujemy, że składamy albo wysyłamy wszystko do urzędu z UPO.',
    },
    {
      q: 'Czym różni się Bookeper od klasycznego programu do faktur?',
      a: 'Wiele programów generuje deklaracje z ewidencji i wysyła je (z UPO) po Twoim kliknięciu; dokumenty wprowadzasz Ty. Bookeper przygotowuje ewidencję i deklaracje do Twojego zatwierdzenia. Porównanie programów znajdziesz w naszym rankingu programów księgowych dla JDG.',
    },
    {
      q: 'Czy mogę zostać przy Excelu?',
      a: 'Arkusz może zostać narzędziem pomocniczym, ale ewidencję trzeba prowadzić w programie, który generuje wymagany plik JPK (JPK_EWP lub JPK_PKPIR). Od kiedy dotyczy to Ciebie, zależy m.in. od tego, czy rozliczasz VAT w JPK_V7M — szczegóły w naszym artykule o ewidencji przychodów na ryczałcie.',
    },
  ],
  bookeper:
    'Wrzucasz faktury — Bookeper liczy podatki i składki, prowadzi ewidencję i przygotowuje deklaracje. Ty sprawdzasz i zatwierdzasz przed złożeniem. Obsługujemy ryczałt, liniowy i skalę. Obsługa kolejnych formularzy jest rozwijana. Cena: 0 zł w otwartej becie.',
  sources: [
    'Ceny biur i programów: nie podajemy uśrednionych widełek — porównuj aktualne cenniki dostawców i wyceny biur dla swojego zakresu usług.',
    'Cena Bookeper — 0 zł w otwartej becie.',
  ],
  disclaimer:
    'Tekst ma charakter informacyjny i nie stanowi porady podatkowej ani prawnej. Ceny biur i programów zależą od zakresu usług i mogą się zmieniać — sprawdzaj aktualne cenniki. W razie wątpliwości skonsultuj się z doradcą lub biurem. Bookeper nie zastępuje doradcy podatkowego.',
  relatedLinks: [
    {
      href: '/blog/ewidencja-przychodow-ryczalt-jak-prowadzic-2026',
      label: 'Ewidencja przychodów na ryczałcie — jak prowadzić w 2026',
    },
    {
      href: '/blog/najlepsze-programy-ksiegowe-jdg-2026',
      label: 'Najlepsze programy księgowe dla JDG 2026',
    },
    { href: '/ksiegowosc-ai', label: 'Księgowość AI: co robi AI, a co liczy kod' },
    { href: '/kalkulatory/ryczalt', label: 'Kalkulator ryczałtu' },
    { href: '/kalkulatory/zus-dra', label: 'Kalkulator ZUS DRA' },
    { href: '/kalkulatory/forma-opodatkowania', label: 'Kalkulator formy opodatkowania' },
  ],
  mentionCta: {
    label: FUNNEL_CTA_LABEL,
    utmCampaign: 'ksiegowosc_online',
    utmContent: 'bookeper_mention',
  },
  footerCta: {
    heading: 'Chcesz rozliczać JDG bez biura?',
    body: `Załóż konto w Bookeper. Otwarta beta: 0 zł, bez karty, dostęp od razu · przygotowujemy deklaracje — Ty zatwierdzasz.`,
    label: FUNNEL_CTA_LABEL,
    utmCampaign: 'ksiegowosc_online',
    utmContent: 'footer',
  },
  stickyCta: {
    label: FUNNEL_CTA_LABEL,
    body: 'Księgowość JDG — 0 zł w otwartej becie',
    utmCampaign: 'ksiegowosc_online_sticky_cta',
    utmContent: 'sticky',
  },
};

export const E_DORECZENIA_JDG: Guide = {
  slug: 'e-doreczenia-jdg-2026',
  seoTitle: 'e-Doręczenia dla JDG — termin 30 września 2026 | Bookeper',
  description:
    'Przedsiębiorcy wpisani do CEIDG do 31 grudnia 2024 muszą mieć adres do doręczeń elektronicznych wpisany do bazy adresów elektronicznych do 30 września 2026. Kogo to dotyczy, od kiedy i jak założyć skrzynkę.',
  h1: 'e-Doręczenia dla JDG — kto musi mieć skrzynkę i do kiedy',
  navLabel: 'e-Doręczenia dla JDG',
  lead: 'Jeżeli Twoja działalność została wpisana do CEIDG do 31 grudnia 2024 r., masz obowiązek uzyskać wpis adresu do doręczeń elektronicznych do bazy adresów elektronicznych w terminie do dnia 30 września 2026 r. Uwaga na często powtarzaną datę 1 października 2026 — to dzień, w którym wchodzi w życie sam obowiązek posiadania adresu, a nie termin na jego założenie. Termin na działanie mija dzień wcześniej. Przedsiębiorcy, którzy złożyli wniosek o wpis do CEIDG od 1 stycznia 2025 r., przekazali dane do utworzenia adresu już razem z wnioskiem i nie mają osobnego terminu.',
  datePublished: '2026-09-03',
  dateModified: '2026-09-03',
  sections: [
    {
      id: 'czym-sa-e-doreczenia',
      heading: 'Czym są e-Doręczenia?',
      body: [
        'e-Doręczenia to elektroniczny odpowiednik listu poleconego za potwierdzeniem odbioru. Korespondencja wysłana na adres do doręczeń elektronicznych wywołuje ten sam skutek prawny co pismo doręczone na papierze — łącznie z liczeniem terminów na odpowiedź czy odwołanie.',
        'Adres do doręczeń elektronicznych to nie jest zwykły e-mail ani skrytka ePUAP. To osobny adres wpisywany do publicznej bazy adresów elektronicznych (BAE), powiązany ze skrzynką doręczeń. Urząd sięga po ten adres do bazy — dlatego samo założenie skrzynki bez wpisu do bazy nie kończy sprawy.',
        'Adres można uzyskać w ramach publicznej usługi rejestrowanego doręczenia elektronicznego (bezpłatnie, przez ministra właściwego do spraw informatyzacji) albo u kwalifikowanego dostawcy usługi rejestrowanego doręczenia elektronicznego.',
      ],
    },
    {
      id: 'kogo-dotyczy-i-do-kiedy',
      heading: 'Kogo dotyczy obowiązek i do kiedy?',
      body: [
        'Termin zależy od tego, kiedy Twoja firma trafiła do rejestru. To najczęstsze źródło nieporozumień, bo w obiegu funkcjonuje kilka dat naraz.',
        'Osobna, wcześniejsza ścieżka dotyczy przedsiębiorców wpisanych do CEIDG do 31 grudnia 2024 r., którzy w okresie od 1 lipca 2025 r. do 30 września 2026 r. składają wniosek zmieniający wpis. W takim przypadku dane do utworzenia adresu przekazuje się już razem z tym wnioskiem — obowiązek powstaje przy tej zmianie, a nie dopiero 30 września 2026 r.',
        'Bookeper nie ma wglądu w historię Twoich wniosków w CEIDG, więc tego warunku nie sprawdzimy za Ciebie. Jeśli od lipca 2025 zmieniałeś, zawieszałeś albo wznawiałeś działalność, sprawdź, czy adres został wtedy przekazany.',
      ],
      bullets: [
        'Wpis do CEIDG do 31 grudnia 2024 r. — adres w bazie do 30 września 2026 r.',
        'Wniosek o wpis do CEIDG od 1 stycznia 2025 r. — dane do utworzenia adresu przekazywane razem z wnioskiem, bez osobnego terminu',
        'Wniosek zmieniający wpis w CEIDG złożony między 1 lipca 2025 r. a 30 września 2026 r. — obowiązek powstaje przy tym wniosku',
        'Spółki wpisane do rejestru przedsiębiorców KRS — termin minął już 1 kwietnia 2025 r.',
      ],
    },
    {
      id: 'co-jesli-nie-zalozysz',
      heading: 'Co się dzieje, jeśli nie założysz skrzynki?',
      body: [
        'Obowiązek posiadania adresu do doręczeń elektronicznych nie wygasa po terminie — nie jest to jednorazowa formalność, która się przedawnia. Po 30 września 2026 r. przedsiębiorca bez wpisanego adresu po prostu pozostaje w stanie niezgodnym z ustawą.',
        'Praktyczna konsekwencja jest inna niż kara: urzędy przechodzą na doręczanie elektroniczne, a korespondencja kierowana na adres do doręczeń wywołuje skutek prawny niezależnie od tego, czy zaglądasz do skrzynki. Brak adresu oznacza, że wracasz do wolniejszej ścieżki papierowej lub hybrydowej, a nie że nic się nie dzieje.',
        'Założenie adresu jest bezpłatne w ramach usługi publicznej, więc odkładanie tego na ostatni tydzień września nie daje żadnej korzyści, a ryzykuje kolejką wniosków.',
      ],
    },
    {
      id: 'jak-zalozyc',
      heading: 'Jak założyć adres do doręczeń elektronicznych?',
      body: [
        'Wniosek o utworzenie adresu w ramach publicznej usługi składa się elektronicznie na stronie gov.pl, w usłudze e-Doręczenia. Potrzebny jest profil zaufany, e-dowód albo podpis kwalifikowany.',
        'We wniosku wskazujesz, czy adres ma dotyczyć działalności gospodarczej wpisanej do CEIDG. To istotne, bo adres firmowy jest powiązany z wpisem w rejestrze, a informacja o nim trafia automatycznie z bazy adresów elektronicznych do CEIDG.',
        'Po utworzeniu adresu i aktywacji skrzynki warto sprawdzić, czy adres faktycznie widnieje w bazie adresów elektronicznych. Sam wniosek nie jest jeszcze wpisem — a to wpis do bazy jest tym, czego wymaga ustawa.',
      ],
      bullets: [
        'Zaloguj się do usługi e-Doręczenia na gov.pl (profil zaufany, e-dowód lub podpis kwalifikowany)',
        'Złóż wniosek o adres do doręczeń elektronicznych dla działalności wpisanej do CEIDG',
        'Aktywuj skrzynkę doręczeń po utworzeniu adresu',
        'Sprawdź, czy adres jest widoczny w bazie adresów elektronicznych — to on jest wymagany, nie sam wniosek',
      ],
    },
  ],
  faq: [
    {
      q: 'Do kiedy JDG musi mieć e-Doręczenia?',
      a: 'Przedsiębiorcy wpisani do CEIDG do 31 grudnia 2024 r. muszą uzyskać wpis adresu do doręczeń elektronicznych do bazy adresów elektronicznych w terminie do dnia 30 września 2026 r. Data 1 października 2026 r., która często pojawia się w publikacjach, to dzień wejścia w życie samego obowiązku posiadania adresu, a nie termin na jego założenie.',
    },
    {
      q: 'Czy e-Doręczenia dotyczą firm założonych w 2025 roku?',
      a: 'Tak, ale bez osobnego terminu. Podmioty składające wniosek o wpis do CEIDG od 1 stycznia 2025 r. przekazują dane niezbędne do utworzenia adresu do doręczeń elektronicznych już razem z wnioskiem o wpis, więc adres powstaje na starcie działalności.',
    },
    {
      q: 'Czy adres do doręczeń elektronicznych to to samo co ePUAP?',
      a: 'Nie. Adres do doręczeń elektronicznych jest wpisywany do bazy adresów elektronicznych i powiązany z publiczną albo kwalifikowaną usługą rejestrowanego doręczenia elektronicznego. Skrzynka ePUAP to osobne rozwiązanie i nie zastępuje adresu do doręczeń.',
    },
    {
      q: 'Czy założenie e-Doręczeń kosztuje?',
      a: 'Adres w ramach publicznej usługi rejestrowanego doręczenia elektronicznego jest bezpłatny. Odpłatne są usługi kwalifikowanych dostawców, z których można skorzystać zamiast usługi publicznej.',
    },
    {
      q: 'Czy zmiana wpisu w CEIDG przyspiesza mój termin?',
      a: 'Tak. Przedsiębiorca wpisany do CEIDG do 31 grudnia 2024 r., który w okresie od 1 lipca 2025 r. do 30 września 2026 r. składa wniosek zmieniający wpis, przekazuje dane do utworzenia adresu już razem z tym wnioskiem. Obowiązek powstaje wtedy przy tej zmianie, a nie dopiero 30 września 2026 r.',
    },
  ],
  bookeper:
    'Bookeper wylicza Twój termin e-Doręczeń z daty rozpoczęcia działalności zapisanej w Twoim profilu: jeśli wpis do CEIDG nastąpił do 31 grudnia 2024 r., termin to 30 września 2026 r. Jeśli tej daty w profilu nie ma, zapytamy o nią, zamiast zgadywać — bez niej nie da się rozstrzygnąć, który termin Cię dotyczy. Skrzynki nie zakładamy za Ciebie: wniosek składasz sam na gov.pl, a my tłumaczymy, co dokładnie jest wymagane.',
  sources: [
    'Ustawa z dnia 18 listopada 2020 r. o doręczeniach elektronicznych — tekst jednolity Dz.U. 2026 poz. 3, art. 152 ust. 3 (termin do 30 września 2026 r. dla podmiotów wpisanych do CEIDG do 31 grudnia 2024 r.)',
    'Tamże, art. 152 ust. 1 (wniosek o wpis do CEIDG od 1 stycznia 2025 r.) oraz art. 152 ust. 2 (wniosek zmieniający wpis w okresie 1 lipca 2025 r. – 30 września 2026 r.)',
    'Tamże, art. 9 ust. 1 pkt 9 — wchodzi w życie z dniem 1 października 2026 r. na podstawie art. 166 pkt 9; to źródło powtarzanej daty 1.10.2026',
    'Tamże, art. 151 ust. 1 w związku z komunikatem Ministra Cyfryzacji z dnia 12 lipca 2024 r. (Dz.U. 2024 poz. 1078), który wyznaczył termin 1 stycznia 2025 r. — stąd 1 kwietnia 2025 r. dla rejestru przedsiębiorców KRS',
  ],
};

/** Ordered — drives the sibling-guide cross-links and the sitemap slug list. */
export const GUIDES: readonly Guide[] = [
  KPIR_ELEKTRONICZNA,
  KOD_PKD,
  KSEF_DLA_JDG,
  KSIEGOWOSC_ONLINE,
  E_DORECZENIA_JDG,
] as const;

export const PRZEWODNIKI_BASE_PATH = '/przewodniki';

/** Canonical (Polish, unprefixed) URL — every locale route points here. */
export function guideUrl(slug: string): string {
  return `${SITE_URL}${PRZEWODNIKI_BASE_PATH}/${slug}`;
}

/** Flat HTML entry filename for a guide (vite rollup input / vercel rewrite target). */
export function guideHtmlEntry(slug: string): string {
  return `przewodnik-${slug}.html`;
}
